import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Field, Input, Select, Switch, Textarea } from '@zt/ui';
import { HelpTip } from '../../lib/help';
import { minorToInput, toMinor } from '../../lib/money';
import type { WorkflowCatalog, WorkflowDetail } from '../../lib/types-faz8';
import { key, useWorkflowText } from './labels';

/**
 * Tetikleyici → Koşul → Eylem düzenleyicisi (F8-15). Denetimli bileşen: tanım üst bileşende tutulur, kaydetme ve
 * doğrulama sunucudadır (sorunlar sayfada listelenir). Her adım numaralı bir kutu; kutular arasında ok vardır.
 */

export type Def = Pick<
  WorkflowDetail,
  'name' | 'description' | 'trigger' | 'conditions' | 'actions' | 'throttleMinutes' | 'maxRunsPerHour'
>;
type Rule = Def['conditions']['rules'][number];
type Action = Def['actions'][number];
type TriggerType = Def['trigger']['type'];
type ActionType = Action['type'];
type Op = Rule['op'];
type Params = Record<string, unknown>;

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

export function triggerDefaults(type: string): Params {
  if (type === 'schedule') return { frequency: 'weekly', weekday: 1, monthDay: null, hour: 8 };
  if (type === 'tls.expiring') return { days: 14 };
  return {};
}

function actionDefaults(type: string, c: WorkflowCatalog): Params {
  switch (type) {
    case 'notify_staff':
      return { recipients: { mode: 'permission', permission: 'org.store.manage' }, subject: '', message: '' };
    case 'seo_check':
      return { stopOnIssues: true };
    case 'merchant_sync':
      return { storeId: c.stores.length === 1 ? c.stores[0]!.id : '' };
    case 'record':
      return { note: '' };
    case 'invoice_draft':
      return { send: false };
    case 'publish_post':
      return { mode: 'next_day', hours: null, hour: 10 };
    case 'send_report':
      return { savedReportId: '', recipientIds: [], format: 'xlsx' };
    default:
      return {};
  }
}

export function WorkflowBuilder({
  value,
  onChange,
  catalog,
  readOnly,
}: {
  value: Def;
  onChange: (d: Def) => void;
  catalog: WorkflowCatalog;
  readOnly: boolean;
}) {
  const w = useWorkflowText();
  const { t } = w;
  const trig = catalog.triggers.find((x) => x.type === value.trigger.type)!;
  const set = (patch: Partial<Def>) => onChange({ ...value, ...patch });
  const setTrigger = (type: TriggerType) => {
    const next = catalog.triggers.find((x) => x.type === type)!;
    const fields = new Set(next.fields.map((f) => f.key));
    set({
      trigger: { type, params: triggerDefaults(type) },
      conditions: { ...value.conditions, rules: value.conditions.rules.filter((r) => fields.has(r.field)) },
    });
  };
  const rules = value.conditions.rules;
  const setRule = (i: number, r: Rule) =>
    set({ conditions: { ...value.conditions, rules: rules.map((x, j) => (j === i ? r : x)) } });
  const actions = value.actions;
  const setAction = (i: number, a: Action) => set({ actions: actions.map((x, j) => (j === i ? a : x)) });
  const move = (i: number, d: -1 | 1) => {
    const next = actions.slice();
    [next[i], next[i + d]] = [next[i + d]!, next[i]!];
    set({ actions: next });
  };
  const usable = (type: string) => {
    const a = catalog.actions.find((x) => x.type === type)!;
    return !a.needs || trig.provides.includes(a.needs);
  };
  const firstUsable = catalog.actions.find((a) => a.allowed && usable(a.type))?.type ?? 'record';

  return (
    <fieldset disabled={readOnly} className="space-y-3">
      <legend className="sr-only">{t('admin.workflows.builderLegend')}</legend>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('admin.workflows.name')} required>
          {(p) => (
            <Input
              {...p}
              value={value.name}
              maxLength={120}
              onChange={(e) => set({ name: e.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.workflows.description')}>
          {(p) => (
            <Input
              {...p}
              value={value.description ?? ''}
              maxLength={500}
              onChange={(e) => set({ description: e.target.value || null })}
            />
          )}
        </Field>
      </div>

      <Step n={1} title={t('admin.workflows.step1')} help={<HelpTip k="workflows.trigger" />}>
        <Field
          label={t('admin.workflows.trigger')}
          description={t(`admin.workflows.triggerHints.${key(trig.type)}`)}
        >
          {(p) => (
            <Select
              {...p}
              value={value.trigger.type}
              onChange={(e) => setTrigger(e.target.value as TriggerType)}
            >
              {catalog.triggers.map((x) => (
                <option key={x.type} value={x.type} disabled={!x.allowed}>
                  {w.trigger(x.type)}
                  {!x.allowed ? ` ${t('admin.workflows.notAllowed')}` : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <TriggerParams
          type={value.trigger.type}
          params={value.trigger.params}
          onChange={(params) => set({ trigger: { ...value.trigger, params } })}
        />
      </Step>
      <Arrow />

      <Step n={2} title={t('admin.workflows.step2')} help={<HelpTip k="workflows.conditions" />}>
        {!trig.fields.length ? (
          <p className="text-sm text-ink-3">{t('admin.workflows.noFields')}</p>
        ) : (
          <>
            {rules.length > 1 && (
              <Field label={t('admin.workflows.match')} className="max-w-xs">
                {(p) => (
                  <Select
                    {...p}
                    value={value.conditions.match}
                    onChange={(e) =>
                      set({ conditions: { ...value.conditions, match: e.target.value as 'all' | 'any' } })
                    }
                  >
                    <option value="all">{t('admin.workflows.matches.all')}</option>
                    <option value="any">{t('admin.workflows.matches.any')}</option>
                  </Select>
                )}
              </Field>
            )}
            {!rules.length && <p className="text-sm text-ink-3">{t('admin.workflows.noConditions')}</p>}
            <ol className="space-y-2">
              {rules.map((r, i) => (
                <li key={i}>
                  <RuleRow
                    n={i + 1}
                    rule={r}
                    fields={trig.fields}
                    operators={catalog.operators}
                    onChange={(x) => setRule(i, x)}
                    onRemove={() =>
                      set({ conditions: { ...value.conditions, rules: rules.filter((_, j) => j !== i) } })
                    }
                  />
                </li>
              ))}
            </ol>
            {rules.length < catalog.limits.rules && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  const f = trig.fields[0]!;
                  set({
                    conditions: {
                      ...value.conditions,
                      rules: [
                        ...rules,
                        { field: f.key, op: catalog.operators[f.type][0]!, value: emptyValue(f) },
                      ],
                    },
                  });
                }}
              >
                {t('admin.workflows.addRule')}
              </Button>
            )}
          </>
        )}
      </Step>
      <Arrow />

      <Step n={3} title={t('admin.workflows.step3')} help={<HelpTip k="workflows.actions" />}>
        <ol className="space-y-3">
          {actions.map((a, i) => (
            <li key={i} className="rounded-md border border-line bg-surface p-3">
              <div className="flex flex-wrap items-end gap-2">
                <Field label={t('admin.workflows.actionN', { n: i + 1 })} className="min-w-[16rem] flex-1">
                  {(p) => (
                    <Select
                      {...p}
                      value={a.type}
                      onChange={(e) => {
                        const type = e.target.value as ActionType;
                        setAction(i, { type, params: actionDefaults(type, catalog) });
                      }}
                    >
                      {catalog.actions.map((x) => (
                        <option key={x.type} value={x.type} disabled={!x.allowed || !usable(x.type)}>
                          {w.action(x.type)}
                          {!usable(x.type)
                            ? ` ${t('admin.workflows.incompatible')}`
                            : !x.allowed
                              ? ` ${t('admin.workflows.notAllowed')}`
                              : ''}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <span className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={i === 0}
                    aria-label={t('admin.workflows.moveUpN', { n: i + 1 })}
                    onClick={() => move(i, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={i === actions.length - 1}
                    aria-label={t('admin.workflows.moveDownN', { n: i + 1 })}
                    onClick={() => move(i, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('admin.workflows.removeActionN', { n: i + 1 })}
                    onClick={() => set({ actions: actions.filter((_, j) => j !== i) })}
                  >
                    {t('common.delete')}
                  </Button>
                </span>
              </div>
              {!usable(a.type) && (
                <p className="mt-1 text-sm text-rose">{t('errors.workflow_action_incompatible')}</p>
              )}
              <p className="mt-1 text-xs text-ink-3">{t(`admin.workflows.actionHints.${a.type}`)}</p>
              <div className="mt-3">
                <ActionParams
                  action={a}
                  catalog={catalog}
                  fields={trig.fields.map((f) => f.key)}
                  onChange={(params) => setAction(i, { ...a, params })}
                />
              </div>
            </li>
          ))}
        </ol>
        {actions.length < catalog.limits.actions && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              set({
                actions: [...actions, { type: firstUsable, params: actionDefaults(firstUsable, catalog) }],
              })
            }
          >
            {t('admin.workflows.addAction')}
          </Button>
        )}
      </Step>

      <details className="rounded-md border border-line p-3">
        <summary className="cursor-pointer text-sm font-medium text-ink">
          {t('admin.workflows.advanced')}
        </summary>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Field
            label={t('admin.workflows.throttle')}
            description={t('admin.workflows.throttleNote')}
            help={<HelpTip k="workflows.limits" />}
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                min={0}
                max={10080}
                value={value.throttleMinutes}
                onChange={(e) =>
                  set({ throttleMinutes: Math.max(0, Math.trunc(Number(e.target.value) || 0)) })
                }
              />
            )}
          </Field>
          <Field label={t('admin.workflows.rate')} description={t('admin.workflows.rateNote')}>
            {(p) => (
              <Input
                {...p}
                type="number"
                min={1}
                max={1000}
                value={value.maxRunsPerHour}
                onChange={(e) =>
                  set({ maxRunsPerHour: Math.max(1, Math.trunc(Number(e.target.value) || 1)) })
                }
              />
            )}
          </Field>
        </div>
      </details>
    </fieldset>
  );
}

function Step({
  n,
  title,
  help,
  children,
}: {
  n: number;
  title: string;
  help: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section aria-label={title} className="rounded-md border border-line-strong bg-surface-2 p-4">
      <h3 className="mb-3 flex items-center gap-2 font-cond text-base font-semibold text-ink">
        <span
          aria-hidden
          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-pine text-xs text-white"
        >
          {n}
        </span>
        {title}
        {help}
      </h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Arrow() {
  return (
    <div aria-hidden className="flex justify-center text-ink-3">
      ↓
    </div>
  );
}

type FieldDef = WorkflowCatalog['triggers'][number]['fields'][number];
const emptyValue = (f: FieldDef): Rule['value'] =>
  f.type === 'bool' ? null : f.type === 'enum' ? (f.options?.[0] ?? '') : f.type === 'text' ? '' : 0;

function RuleRow({
  n,
  rule,
  fields,
  operators,
  onChange,
  onRemove,
}: {
  n: number;
  rule: Rule;
  fields: WorkflowCatalog['triggers'][number]['fields'];
  operators: WorkflowCatalog['operators'];
  onChange: (r: Rule) => void;
  onRemove: () => void;
}) {
  const w = useWorkflowText();
  const { t } = w;
  const f = fields.find((x) => x.key === rule.field) ?? fields[0]!;
  const [money, setMoney] = useState(() =>
    f.type === 'money' && typeof rule.value === 'number' ? minorToInput(rule.value) : '',
  );
  return (
    <div
      role="group"
      aria-label={t('admin.workflows.ruleN', { n })}
      className="flex flex-wrap items-end gap-2"
    >
      <Field label={t('admin.workflows.ruleField')} className="min-w-[11rem]">
        {(p) => (
          <Select
            {...p}
            value={f.key}
            onChange={(e) => {
              const nf = fields.find((x) => x.key === e.target.value)!;
              setMoney('');
              onChange({ field: nf.key, op: operators[nf.type][0]!, value: emptyValue(nf) });
            }}
          >
            {fields.map((x) => (
              <option key={x.key} value={x.key}>
                {w.field(x.key)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label={t('admin.workflows.ruleOp')} className="min-w-[9rem]">
        {(p) => (
          <Select {...p} value={rule.op} onChange={(e) => onChange({ ...rule, op: e.target.value as Op })}>
            {operators[f.type].map((o) => (
              <option key={o} value={o}>
                {t(`admin.workflows.ops.${o}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {f.type !== 'bool' && (
        <Field
          label={f.type === 'money' ? t('admin.workflows.ruleValueMoney') : t('admin.workflows.ruleValue')}
          className="min-w-[10rem] flex-1"
        >
          {(p) =>
            f.type === 'enum' ? (
              <Select
                {...p}
                value={String(rule.value ?? '')}
                onChange={(e) => onChange({ ...rule, value: e.target.value })}
              >
                {(f.options ?? []).map((o) => (
                  <option key={o} value={o}>
                    {w.option(o)}
                  </option>
                ))}
              </Select>
            ) : f.type === 'money' ? (
              <Input
                {...p}
                inputMode="decimal"
                value={money}
                onChange={(e) => {
                  setMoney(e.target.value);
                  onChange({ ...rule, value: toMinor(e.target.value, 'TRY') ?? 0 });
                }}
              />
            ) : f.type === 'number' ? (
              <Input
                {...p}
                type="number"
                value={typeof rule.value === 'number' ? rule.value : ''}
                onChange={(e) => onChange({ ...rule, value: Math.trunc(Number(e.target.value) || 0) })}
              />
            ) : (
              <Input
                {...p}
                value={String(rule.value ?? '')}
                maxLength={200}
                onChange={(e) => onChange({ ...rule, value: e.target.value })}
              />
            )
          }
        </Field>
      )}
      <Button
        size="sm"
        variant="ghost"
        aria-label={t('admin.workflows.removeRuleN', { n })}
        onClick={onRemove}
      >
        {t('common.delete')}
      </Button>
    </div>
  );
}

function TriggerParams({
  type,
  params,
  onChange,
}: {
  type: string;
  params: Params;
  onChange: (p: Params) => void;
}) {
  const { t } = useWorkflowText();
  if (type === 'tls.expiring')
    return (
      <Field label={t('admin.workflows.params.days')} className="max-w-xs">
        {(p) => (
          <Input
            {...p}
            type="number"
            min={1}
            max={60}
            value={Number(params.days ?? 14)}
            onChange={(e) => onChange({ days: Math.trunc(Number(e.target.value) || 14) })}
          />
        )}
      </Field>
    );
  if (type !== 'schedule') return null;
  const freq = String(params.frequency ?? 'weekly');
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Field label={t('admin.workflows.params.frequency')}>
        {(p) => (
          <Select
            {...p}
            value={freq}
            onChange={(e) =>
              onChange({
                ...params,
                frequency: e.target.value,
                weekday: e.target.value === 'weekly' ? (params.weekday ?? 1) : null,
                monthDay: e.target.value === 'monthly' ? (params.monthDay ?? 1) : null,
              })
            }
          >
            {(['daily', 'weekly', 'monthly'] as const).map((f) => (
              <option key={f} value={f}>
                {t(`admin.workflows.frequencies.${f}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {freq === 'weekly' && (
        <Field label={t('admin.workflows.params.weekday')}>
          {(p) => (
            <Select
              {...p}
              value={Number(params.weekday ?? 1)}
              onChange={(e) => onChange({ ...params, weekday: Number(e.target.value) })}
            >
              {WEEKDAYS.map((d) => (
                <option key={d} value={d}>
                  {t(`admin.workflows.weekdays.${d}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      {freq === 'monthly' && (
        <Field label={t('admin.workflows.params.monthDay')}>
          {(p) => (
            <Select
              {...p}
              value={Number(params.monthDay ?? 1)}
              onChange={(e) => onChange({ ...params, monthDay: Number(e.target.value) })}
            >
              {MONTH_DAYS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
      <Field label={t('admin.workflows.params.hour')}>
        {(p) => (
          <Select
            {...p}
            value={Number(params.hour ?? 8)}
            onChange={(e) => onChange({ ...params, hour: Number(e.target.value) })}
          >
            {HOURS.map((h) => (
              <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>
            ))}
          </Select>
        )}
      </Field>
    </div>
  );
}

function MemberChecks({
  legend,
  members,
  selected,
  onChange,
}: {
  legend: string;
  members: WorkflowCatalog['members'];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-medium text-ink">{legend}</legend>
      <ul className="mt-1 grid gap-1 sm:grid-cols-2">
        {members.map((m) => (
          <li key={m.userId}>
            <label className="flex items-center gap-2 text-sm text-ink-2">
              <input
                type="checkbox"
                checked={selected.includes(m.userId)}
                onChange={() =>
                  onChange(
                    selected.includes(m.userId)
                      ? selected.filter((x) => x !== m.userId)
                      : [...selected, m.userId],
                  )
                }
              />
              {m.name}
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  );
}

function ActionParams({
  action,
  catalog,
  fields,
  onChange,
}: {
  action: Action;
  catalog: WorkflowCatalog;
  fields: string[];
  onChange: (p: Params) => void;
}) {
  const w = useWorkflowText();
  const { t } = w;
  const p = action.params as Params;
  const set = (patch: Params) => onChange({ ...p, ...patch });
  switch (action.type) {
    case 'notify_staff': {
      const r = (p.recipients ?? { mode: 'permission', permission: 'org.store.manage' }) as {
        mode: 'permission' | 'users';
        permission?: string;
        userIds?: string[];
      };
      return (
        <div className="space-y-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('admin.workflows.params.recipients')} help={<HelpTip k="workflows.recipients" />}>
              {(fp) => (
                <Select
                  {...fp}
                  value={r.mode}
                  onChange={(e) =>
                    set({
                      recipients:
                        e.target.value === 'users'
                          ? { mode: 'users', userIds: [] }
                          : { mode: 'permission', permission: 'org.store.manage' },
                    })
                  }
                >
                  <option value="permission">{t('admin.workflows.recipientsModes.permission')}</option>
                  <option value="users">{t('admin.workflows.recipientsModes.users')}</option>
                </Select>
              )}
            </Field>
            {r.mode === 'permission' && (
              <Field label={t('admin.workflows.params.permission')}>
                {(fp) => (
                  <Select
                    {...fp}
                    value={r.permission}
                    onChange={(e) => set({ recipients: { mode: 'permission', permission: e.target.value } })}
                  >
                    {catalog.notifyPermissions.map((k) => (
                      <option key={k} value={k}>
                        {t(`permissions.${k}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
          </div>
          {r.mode === 'users' && (
            <MemberChecks
              legend={t('admin.workflows.params.userIds')}
              members={catalog.members}
              selected={r.userIds ?? []}
              onChange={(userIds) => set({ recipients: { mode: 'users', userIds } })}
            />
          )}
          <Field
            label={t('admin.workflows.params.subject')}
            description={
              fields.length
                ? t('admin.workflows.placeholders', { list: fields.map((f) => `{{${f}}}`).join(' ') })
                : undefined
            }
            required
          >
            {(fp) => (
              <Input
                {...fp}
                value={String(p.subject ?? '')}
                maxLength={150}
                onChange={(e) => set({ subject: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.workflows.params.message')}>
            {(fp) => (
              <Textarea
                {...fp}
                rows={3}
                maxLength={2000}
                value={String(p.message ?? '')}
                onChange={(e) => set({ message: e.target.value })}
              />
            )}
          </Field>
        </div>
      );
    }
    case 'seo_check':
      return (
        <label className="flex items-center gap-2 text-sm text-ink-2">
          <Switch
            checked={p.stopOnIssues !== false}
            label={t('admin.workflows.params.stopOnIssues')}
            onCheckedChange={(v) => set({ stopOnIssues: v })}
          />
          {t('admin.workflows.params.stopOnIssues')}
        </label>
      );
    case 'merchant_sync':
      return (
        <Field label={t('admin.workflows.params.storeId')} className="max-w-sm">
          {(fp) => (
            <Select
              {...fp}
              value={String(p.storeId ?? '')}
              onChange={(e) => set({ storeId: e.target.value })}
            >
              <option value="">{t('admin.workflows.choose')}</option>
              {catalog.stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      );
    case 'record':
      return (
        <Field label={t('admin.workflows.params.note')}>
          {(fp) => (
            <Input
              {...fp}
              value={String(p.note ?? '')}
              maxLength={300}
              onChange={(e) => set({ note: e.target.value })}
            />
          )}
        </Field>
      );
    case 'order_processing':
      return null;
    case 'invoice_draft':
      return (
        <label className="flex items-center gap-2 text-sm text-ink-2">
          <Switch
            checked={p.send === true}
            label={t('admin.workflows.params.send')}
            onCheckedChange={(v) => set({ send: v })}
          />
          {t('admin.workflows.params.send')}
        </label>
      );
    case 'publish_post': {
      const mode = String(p.mode ?? 'next_day');
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('admin.workflows.params.mode')}>
            {(fp) => (
              <Select
                {...fp}
                value={mode}
                onChange={(e) =>
                  set({
                    mode: e.target.value,
                    hours: e.target.value === 'delay' ? (p.hours ?? 2) : null,
                    hour: e.target.value === 'next_day' ? (p.hour ?? 10) : null,
                  })
                }
              >
                {(['now', 'delay', 'next_day'] as const).map((m) => (
                  <option key={m} value={m}>
                    {t(`admin.workflows.publishModes.${m}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {mode === 'delay' && (
            <Field label={t('admin.workflows.params.hours')}>
              {(fp) => (
                <Input
                  {...fp}
                  type="number"
                  min={1}
                  max={168}
                  value={Number(p.hours ?? 2)}
                  onChange={(e) => set({ hours: Math.trunc(Number(e.target.value) || 1) })}
                />
              )}
            </Field>
          )}
          {mode === 'next_day' && (
            <Field label={t('admin.workflows.params.hour')}>
              {(fp) => (
                <Select
                  {...fp}
                  value={Number(p.hour ?? 10)}
                  onChange={(e) => set({ hour: Number(e.target.value) })}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>
                  ))}
                </Select>
              )}
            </Field>
          )}
        </div>
      );
    }
    case 'send_report':
      return (
        <div className="space-y-3">
          {!catalog.savedReports.length ? (
            <p className="text-sm text-amber">
              {t('admin.workflows.reportNone')}{' '}
              <Link to="/rapor-olusturucu" className="font-medium text-pine hover:underline">
                {t('admin.reportWidgets.goBuilder')}
              </Link>
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('admin.workflows.params.savedReportId')}>
                {(fp) => (
                  <Select
                    {...fp}
                    value={String(p.savedReportId ?? '')}
                    onChange={(e) => set({ savedReportId: e.target.value })}
                  >
                    <option value="">{t('admin.workflows.choose')}</option>
                    {catalog.savedReports.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('admin.workflows.params.format')}>
                {(fp) => (
                  <Select
                    {...fp}
                    value={String(p.format ?? 'xlsx')}
                    onChange={(e) => set({ format: e.target.value })}
                  >
                    <option value="xlsx">XLSX</option>
                    <option value="csv">CSV</option>
                  </Select>
                )}
              </Field>
            </div>
          )}
          <MemberChecks
            legend={t('admin.workflows.params.recipientIds')}
            members={catalog.members}
            selected={(p.recipientIds as string[] | undefined) ?? []}
            onChange={(recipientIds) => set({ recipientIds })}
          />
        </div>
      );
    default:
      return <Badge tone="rose">{action.type}</Badge>;
  }
}
